import { useCallback, useEffect, useRef, useState } from 'react';
import { CAPTURE_PROTOCOL_VERSION, type CreateScanRequest, type ScanDto } from '@compflash/contracts';
import { ApiError } from '@compflash/client';
import { CAMERA_THRESHOLDS, assessFrame } from '@compflash/domain';
import { useSession } from '../lib/session';
import { fileToBase64 } from '../lib/format';
import { ResultPanel } from '../components/ResultPanel';

/**
 * Live scan workspace.
 *
 * Optimised for one thing: getting from "a card is on screen" to "should I bid"
 * in as few actions as possible. Paste, drop, pick a file or use the webcam,
 * with the auction context kept on screen so the ceiling updates without a
 * re-scan.
 */
export function WorkspacePage(): JSX.Element {
  const { client, usage, refreshAccount } = useSession();
  const [preview, setPreview] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stageLog, setStageLog] = useState<Array<{ stage: string; elapsedMs: number }>>([]);
  const [cameraOn, setCameraOn] = useState(false);

  const [currentBid, setCurrentBid] = useState('');
  const [buyerPremium, setBuyerPremium] = useState('0');
  const [shipping, setShipping] = useState('5.00');
  const [salesTax, setSalesTax] = useState('0');
  const [riskProfile, setRiskProfile] = useState<'conservative' | 'balanced' | 'aggressive'>('balanced');
  const [intent, setIntent] = useState<'collect' | 'flip'>('collect');

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0);

  const runScan = useCallback(
    async (base64: string, mimeType: string) => {
      setBusy(true);
      setError(null);
      setScan(null);
      setStageLog([]);
      startedAtRef.current = performance.now();

      const bidMinor = toMinor(currentBid);
      const request: CreateScanRequest = {
        clientProtocolVersion: CAPTURE_PROTOCOL_VERSION,
        captureTrigger: 'manual',
        imageBase64: base64,
        imageMimeType: (mimeType === 'image/png' || mimeType === 'image/webp' ? mimeType : 'image/jpeg') as CreateScanRequest['imageMimeType'],
        source: 'web_app',
        auction: {
          platform: 'web',
          currentBid: bidMinor === null ? null : { amountMinor: bidMinor, currency: 'USD' },
          currency: 'USD',
          buyerPremiumRate: clampRate(buyerPremium, 0.5),
          shippingEstimate: { amountMinor: toMinor(shipping) ?? 0, currency: 'USD' },
          salesTaxRate: clampRate(salesTax, 0.3),
          observedAt: new Date().toISOString(),
          sourceUrl: null,
        },
        preferences: { riskProfile, intent },
      };

      try {
        await client.createScanStreaming(request, (next) => {
          setScan(next);
          setStageLog((log) => [...log, { stage: next.stage, elapsedMs: Math.round(performance.now() - startedAtRef.current) }]);
        });
        await refreshAccount();
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught.isQuotaExceeded
              ? `${caught.message} See the Billing page to upgrade.`
              : caught.message
            : String(caught),
        );
      } finally {
        setBusy(false);
      }
    },
    [buyerPremium, client, currentBid, intent, refreshAccount, riskProfile, salesTax, shipping],
  );

  const handleFile = useCallback(
    async (file: Blob) => {
      const { base64, mimeType } = await fileToBase64(file);
      setPreview(`data:${mimeType};base64,${base64}`);
      await runScan(base64, mimeType);
    },
    [runScan],
  );

  // Paste is the fastest possible path from a screenshot to a verdict.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent): void => {
      const item = [...(event.clipboardData?.items ?? [])].find((entry) => entry.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (file) void handleFile(file);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handleFile]);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const startCamera = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 } } });
      streamRef.current = stream;
      setCameraOn(true);
      queueMicrotask(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      });
    } catch (caught) {
      setError(`Camera unavailable: ${(caught as Error).message}`);
    }
  };

  const stopCamera = (): void => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  const captureFromCamera = async (): Promise<void> => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return;
    context.drawImage(video, 0, 0);

    // Same quality gate the extension and the server use, so the user is told
    // about a blurred capture before it costs them a scan.
    const metrics = measure(context, canvas.width, canvas.height);
    const assessment = assessFrame(metrics, { thresholds: CAMERA_THRESHOLDS });
    if (!assessment.usable) {
      setError(assessment.issues.filter((issue) => issue.severity === 'reject').map((issue) => issue.hint).join(' '));
      return;
    }

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (blob) await handleFile(blob);
  };

  const identification = scan?.identification ?? null;

  return (
    <div className="workspace">
      <section className="panel capture-panel">
        <h2>Scan a card</h2>
        <p className="muted small">
          Paste a screenshot (Ctrl/Cmd+V), drop an image, choose a file, or use your camera. Blurred and moving frames are
          rejected before they cost you a scan.
        </p>

        <div
          className="dropzone"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            const file = event.dataTransfer.files[0];
            if (file) void handleFile(file);
          }}
        >
          {preview ? <img src={preview} alt="Captured card" /> : <span className="muted">Drop or paste a card image here</span>}
        </div>

        <div className="row">
          <label className="file-button">
            Choose image
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
          </label>
          {cameraOn ? (
            <>
              <button type="button" className="primary" onClick={() => void captureFromCamera()} disabled={busy}>
                Capture
              </button>
              <button type="button" className="ghost" onClick={stopCamera}>
                Stop camera
              </button>
            </>
          ) : (
            <button type="button" className="ghost" onClick={() => void startCamera()}>
              Use camera
            </button>
          )}
        </div>

        {cameraOn && <video ref={videoRef} className="camera" muted playsInline />}

        <h3>Auction context</h3>
        <div className="field-grid">
          <Field label="Current bid ($)" value={currentBid} onChange={setCurrentBid} placeholder="e.g. 45.00" />
          <Field label="Buyer premium (0–0.5)" value={buyerPremium} onChange={setBuyerPremium} />
          <Field label="Shipping ($)" value={shipping} onChange={setShipping} />
          <Field label="Sales tax (0–0.3)" value={salesTax} onChange={setSalesTax} />
          <label className="field">
            <span>Risk profile</span>
            <select value={riskProfile} onChange={(event) => setRiskProfile(event.target.value as typeof riskProfile)}>
              <option value="conservative">Conservative</option>
              <option value="balanced">Balanced</option>
              <option value="aggressive">Aggressive</option>
            </select>
          </label>
          <label className="field">
            <span>Intent</span>
            <select value={intent} onChange={(event) => setIntent(event.target.value as typeof intent)}>
              <option value="collect">Collecting</option>
              <option value="flip">Flipping</option>
            </select>
          </label>
        </div>

        {usage && (
          <p className="muted small">
            {usage.scansPerDay === null
              ? `${usage.scansUsed} scans this month (unlimited plan).`
              : `${usage.scansUsedToday} of ${usage.scansPerDay} scans used today · ${usage.scansUsed} this month.`}
          </p>
        )}
        {error && <p className="error">{error}</p>}
      </section>

      <div className="result-column">
        {busy && !scan && <div className="panel"><p className="muted">Uploading…</p></div>}

        {stageLog.length > 0 && (
          <div className="panel stage-log">
            <h3>Progress</h3>
            <ol>
              {stageLog.map((entry, index) => (
                <li key={`${entry.stage}-${index}`}>
                  <span>{entry.stage.replace(/_/g, ' ')}</span>
                  <span className="mono">{entry.elapsedMs} ms</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {scan?.stage === 'failed' && (
          <div className="panel">
            <h2>Scan failed</h2>
            <p className="error">{scan.error?.message}</p>
            {scan.quality && (
              <ul className="muted small">
                {scan.quality.issues.map((issue) => (
                  <li key={issue.code}>{issue.hint}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {scan && scan.stage !== 'failed' && identification && (
          <ResultPanel
            identity={(identification.best?.identity ?? null) as Record<string, unknown> | null}
            condition={identification.condition as never}
            identityConfidence={identification.best?.confidence ?? null}
            abstained={identification.abstained}
            abstentionReason={identification.abstentionReason}
            valuation={scan.valuation}
            recommendation={scan.recommendation}
            timings={scan.timings}
            stage={scan.stage}
            onSave={() => void client.setScanSaved(scan.id, true)}
            onCorrect={() => {
              const note = window.prompt('What card is it actually? This correction improves future results.');
              if (note) void client.sendFeedback(scan.id, { kind: 'wrong_card', note });
            }}
          />
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <label className="field">
      <span>{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} inputMode="decimal" />
    </label>
  );
}

function toMinor(value: string): number | null {
  const parsed = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}

function clampRate(value: string, max: number): number {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(max, Math.max(0, parsed));
}

/** Compute the same frame metrics the extension computes, for the camera path. */
function measure(context: CanvasRenderingContext2D, width: number, height: number): Parameters<typeof assessFrame>[0] {
  const sampleWidth = Math.min(192, width);
  const sampleHeight = Math.max(1, Math.round((height / width) * sampleWidth));
  const scratch = document.createElement('canvas');
  scratch.width = sampleWidth;
  scratch.height = sampleHeight;
  const scratchContext = scratch.getContext('2d', { willReadFrequently: true });
  if (!scratchContext) {
    return {
      width,
      height,
      sharpness: 1000,
      brightness: 128,
      clippedHighlightRatio: 0,
      clippedShadowRatio: 0,
      motionScore: null,
      cardCoverage: null,
      occlusionScore: null,
      perceptualHash: null,
    };
  }
  scratchContext.drawImage(context.canvas, 0, 0, sampleWidth, sampleHeight);
  const pixels = scratchContext.getImageData(0, 0, sampleWidth, sampleHeight);

  const gray = new Uint8ClampedArray(sampleWidth * sampleHeight);
  for (let i = 0, p = 0; i < pixels.data.length; i += 4, p += 1) {
    gray[p] = (pixels.data[i]! * 299 + pixels.data[i + 1]! * 587 + pixels.data[i + 2]! * 114) / 1000;
  }

  let brightSum = 0;
  let highlights = 0;
  let shadows = 0;
  for (let i = 0; i < gray.length; i += 1) {
    const value = gray[i]!;
    brightSum += value;
    if (value >= 250) highlights += 1;
    else if (value <= 5) shadows += 1;
  }

  let sum = 0;
  let sumSquares = 0;
  let count = 0;
  for (let y = 1; y < sampleHeight - 1; y += 1) {
    for (let x = 1; x < sampleWidth - 1; x += 1) {
      const index = y * sampleWidth + x;
      const value = 4 * gray[index]! - gray[index - 1]! - gray[index + 1]! - gray[index - sampleWidth]! - gray[index + sampleWidth]!;
      sum += value;
      sumSquares += value * value;
      count += 1;
    }
  }
  const mean = count === 0 ? 0 : sum / count;
  const sharpness = count === 0 ? 0 : sumSquares / count - mean * mean;

  return {
    width,
    height,
    sharpness,
    brightness: brightSum / Math.max(1, gray.length),
    clippedHighlightRatio: highlights / Math.max(1, gray.length),
    clippedShadowRatio: shadows / Math.max(1, gray.length),
    motionScore: null,
    cardCoverage: null,
    occlusionScore: null,
    perceptualHash: null,
  };
}
